import type { RootState } from 'reducers/root';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import initialState from './initialState';
import { resetReduxState } from 'actions/workflows/workflows';
import type { LiveChatIntegration, LiveChatStatus } from 'api/types';
import {
  selectIsLatestStepZendeskCsat,
  selectLastWidgetComponent,
  selectWidgetComponents,
  selectWidgetMode,
} from 'reducers/workflows';
import type { DeploymentConfiguration } from 'components/salesforce-messaging-one-chat/types';

interface SuncoState {
  integrationConfig: {
    integrationId: string;
  };
}

interface CsatResult {
  additional_feedback?: string;
  csat_score?: number;
}

export interface LiveChatState extends SuncoState {
  agentIcon: string | undefined;
  agentName: string;
  agentTyping: boolean;
  // The helpdesk's chat ID
  chatId: string;
  deploymentConfiguration: DeploymentConfiguration | null;
  integration: LiveChatIntegration | undefined;
  isEndLiveChatModalVisible: boolean;
  isInAssistantMode: boolean; // Used only by Kustomer
  jwt: string; // Used only by Salesforce Messaging
  liveChatQuery: string;
  queuePosition: number | undefined;
  status: LiveChatStatus;
  submitted_csat_result: CsatResult;
  userEndedChat: boolean;
  waitTimeMessage: string | null;
}

const liveChatSlice = createSlice({
  extraReducers: builder => {
    builder.addCase(resetReduxState, () => ({
      ...initialState,
      integrationConfig: { integrationId: '' },
    }));
  },
  initialState,
  name: 'liveChat',
  reducers: {
    onAgentConnected(
      state,
      action: PayloadAction<{ agentIcon?: string; agentName?: string }>,
    ) {
      switch (state.integration) {
        case 'sunco':
          // sunco should not be active again after it has ended
          if (state.status !== 'ended') {
            state.status = 'active';
          }
          break;
        default:
          state.status = 'active';
      }

      if (action.payload.agentIcon) {
        state.agentIcon = action.payload.agentIcon;
      }
      if (action.payload.agentName) {
        state.agentName = action.payload.agentName;
      }
    },
    onZopimConnected(state) {
      if (state.status === 'inactive') {
        state.status = 'active';
      }
    },
    resetLiveChatState() {
      return {
        ...initialState,
        integrationConfig: { integrationId: '' },
      };
    },
    setAgentInfo(
      state,
      action: PayloadAction<{ agentIcon: string; agentName: string }>,
    ) {
      state.agentIcon = action.payload.agentIcon;
      state.agentName = action.payload.agentName;
    },
    setDeploymentConfiguration(
      state,
      action: PayloadAction<DeploymentConfiguration | null>,
    ) {
      state.deploymentConfiguration = action.payload;
    },
    setIntegrationId(state, action: PayloadAction<string>) {
      state.integrationConfig.integrationId = action.payload;
    },
    setIsAgentTyping(state, action: PayloadAction<boolean>) {
      if (state.status === 'active') {
        state.agentTyping = action.payload;
      }
    },
    setIsEndLiveChatModalVisible(state, action: PayloadAction<boolean>) {
      state.isEndLiveChatModalVisible = action.payload;
    },
    setIsInAssistantMode(state, action: PayloadAction<boolean>) {
      state.isInAssistantMode = action.payload;
    },
    setLiveChatId(state, action: PayloadAction<string>) {
      state.chatId = action.payload;
    },
    setLiveChatIdAndStatus(
      state,
      action: PayloadAction<{ chatId: string; status: LiveChatStatus }>,
    ) {
      state.chatId = action.payload.chatId;
      state.status = action.payload.status;
    },
    setLiveChatIntegration(state, action: PayloadAction<LiveChatIntegration>) {
      state.integration = action.payload;
    },
    setLiveChatJwt(state, action: PayloadAction<string>) {
      state.jwt = action.payload;
    },
    setLiveChatQuery(state, action: PayloadAction<string>) {
      state.liveChatQuery = action.payload;
    },
    setLiveChatStatus(state, action: PayloadAction<LiveChatStatus>) {
      state.status = action.payload;
    },
    setQueuePosition(state, action: PayloadAction<number>) {
      state.queuePosition = action.payload;
    },
    setSubmittedCsatResult(state, action: PayloadAction<CsatResult>) {
      if (action.payload.csat_score !== undefined) {
        state.submitted_csat_result.csat_score = action.payload.csat_score;
      }

      if (action.payload.additional_feedback !== undefined) {
        state.submitted_csat_result.additional_feedback =
          action.payload.additional_feedback;
      }
    },
    setUserEndedChat(state, action: PayloadAction<boolean>) {
      state.userEndedChat = action.payload;
    },
    setWaitTimeMessage(state, action: PayloadAction<string>) {
      state.waitTimeMessage = action.payload;
    },
  },
});

export const {
  onAgentConnected,
  onZopimConnected,
  resetLiveChatState,
  setAgentInfo,
  setDeploymentConfiguration,
  setIntegrationId,
  setIsAgentTyping,
  setIsEndLiveChatModalVisible,
  setIsInAssistantMode,
  setLiveChatId,
  setLiveChatIdAndStatus,
  setLiveChatIntegration,
  setLiveChatJwt,
  setLiveChatQuery,
  setLiveChatStatus,
  setQueuePosition,
  setSubmittedCsatResult,
  setUserEndedChat,
  setWaitTimeMessage,
} = liveChatSlice.actions;

export const selectChatId = (state: RootState) => state.liveChat.chatId;

export const selectLiveChatStatus = (state: RootState) => state.liveChat.status;

export const selectIntegrationConfig = (state: RootState) =>
  state.liveChat.integrationConfig;

export const selectLiveChatAgentIcon = (state: RootState): string | undefined =>
  state.liveChat.agentIcon;

export const selectLiveChatAgentName = (state: RootState): string =>
  state.liveChat.agentName;

export const selectIsSuncoLiveChatActive = (state: RootState): boolean =>
  Boolean(
    state.liveChat.chatId && state.liveChat.integrationConfig.integrationId,
  );

export const selectLiveChatIntegration = (
  state: RootState,
): LiveChatState['integration'] => state.liveChat.integration;

export const selectQueuePosiition = (
  state: RootState,
): LiveChatState['queuePosition'] => state.liveChat.queuePosition;

export const selectIsEndLiveChatModalVisible = (
  state: RootState,
): LiveChatState['isEndLiveChatModalVisible'] =>
  state.liveChat.isEndLiveChatModalVisible;

export const selectIsInAssistantMode = (
  state: RootState,
): LiveChatState['isInAssistantMode'] => state.liveChat.isInAssistantMode;

export const selectAreAgentTypingDotsVisible = (state: RootState): boolean =>
  state.liveChat.status === 'active' && state.liveChat.agentTyping;

export const selectUserEndedChat = (state: RootState): boolean =>
  state.liveChat.userEndedChat;

export const selectLiveChatQuery = (state: RootState): string =>
  state.liveChat.liveChatQuery;

export const selectLiveChatJwt = (state: RootState): string =>
  state.liveChat.jwt;

export const selectWaitTimeMessage = (state: RootState): string | null =>
  state.liveChat.waitTimeMessage;

export const selectLiveChatWidgetComponentExists = (state: RootState) => {
  const widgetComponents = selectWidgetComponents(state);

  return widgetComponents.some(
    component =>
      component.component_type === 'forethought_live_chat_handoff' ||
      component.component_type === 'sunco_live_chat',
  );
};

export const selectIsLiveChatInputVisible = (state: RootState): boolean => {
  const liveChatIntegration = selectLiveChatIntegration(state);
  const lastWidgetComponent = selectLastWidgetComponent(state);
  const widgetMode = selectWidgetMode(state);

  const lastComponentType = lastWidgetComponent?.component_type;

  return Boolean(
    widgetMode === 'liveChat' &&
      !(
        /**
         * Do not show live chat input for buttons or forms
         * on helpdesks where we support
         */
        (
          (liveChatIntegration === 'kustomer' ||
            liveChatIntegration === 'sunco') &&
          (lastComponentType === 'buttons' || lastComponentType === 'form')
        )
      ),
  );
};

export const selectIsLiveChatInputDisabled = (state: RootState): boolean => {
  const liveChatQuery = selectLiveChatQuery(state);
  const status = selectLiveChatStatus(state);
  const isLatestStepZendeskCsat = selectIsLatestStepZendeskCsat(state);
  const userEndedChat = selectUserEndedChat(state);

  return (
    liveChatQuery.trim() === '' ||
    status !== 'active' ||
    isLatestStepZendeskCsat ||
    userEndedChat
  );
};

export const selectDeploymentConfiguration = (
  state: RootState,
): LiveChatState['deploymentConfiguration'] =>
  state.liveChat.deploymentConfiguration;

export default liveChatSlice.reducer;
